import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../auth/api';
import { useAuth } from '../auth/AuthContext';
import FriendBadge from '../components/FriendBadge';
import { useDocumentTitle } from '../lib/useDocumentTitle';

interface Row {
  rank: number;
  user_id: string;
  handle: string;
  score: number;
  is_friend: boolean;
  is_self: boolean;
}

function todayIso(): string {
  // Server defaults to today (UTC) when no date param is sent. Matching that
  // here keeps the picker initial state aligned with the response.
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

const LeaderboardPage: React.FC = () => {
  useDocumentTitle('leaderboard');
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const idToken = authState.kind === 'authenticated' ? authState.idToken : undefined;

  const [date, setDate] = useState<string>(todayIso());
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!idToken) return;
    setLoading(true); setError('');
    try {
      const res = await api(`/api/leaderboard?date=${encodeURIComponent(date)}`, { token: idToken });
      if (!res.ok) { setError(`load failed (${res.status})`); return; }
      const data = await res.json();
      setRows(data.rows ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed');
    } finally {
      setLoading(false);
    }
  }, [idToken, date]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--paper-2)' }}>
      <div style={{ padding: '12px 18px', borderBottom: '1.4px dashed var(--ink)', background: 'var(--paper)', flexShrink: 0 }}>
        <div className="mono-cap">
          <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/')}>map</span>
          {' › '}leaderboard
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>🏆 daily leaderboard</h1>
          <span style={{ flex: 1 }} />
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            style={{
              padding: '4px 8px',
              border: '1.4px solid var(--ink)',
              borderRadius: 8,
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 12,
              background: 'var(--paper)',
              color: 'var(--ink)',
            }}
          />
        </div>
        <div className="mono-cap" style={{ marginTop: 4 }}>
          top 10 by xp earned · friends marked with the chip
        </div>
      </div>

      <div className="scroll-y" style={{ flex: 1, padding: 12 }}>
        {error && <div style={{ fontSize: 11, color: 'var(--w-coral)', marginBottom: 8 }}>{error}</div>}
        {loading && <div style={{ fontSize: 11, color: 'var(--ink-faint)' }}>loading…</div>}

        {!loading && rows.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-faint)' }}>
            no scores recorded for {date} yet — solve a problem to claim rank #1
          </div>
        )}

        {rows.map(r => {
          const cleanHandle = r.handle.replace(/^@/, '');
          return (
            <div
              key={r.user_id}
              className={`sk-box tight ${r.is_self ? 'amber' : ''}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                marginBottom: 6,
              }}
            >
              <div style={{
                width: 32, height: 32,
                border: '1.5px solid var(--ink)',
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 13,
                background: r.rank === 1 ? 'var(--w-amber)' : r.rank <= 3 ? 'var(--w-cream)' : 'var(--paper)',
                flexShrink: 0,
              }}>
                {r.rank}
              </div>
              <Link
                to={r.is_self ? '/profile' : `/u/${cleanHandle}`}
                style={{ flex: 1, color: 'inherit', textDecoration: 'none', minWidth: 0 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    @{cleanHandle}
                  </span>
                  {r.is_self && <span className="sk-pill amber" style={{ fontSize: 10 }}>you</span>}
                  <FriendBadge active={r.is_friend && !r.is_self} />
                </div>
              </Link>
              <span className="sk-pill amber" style={{ fontSize: 11 }}>{r.score.toLocaleString()} xp</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LeaderboardPage;
